import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionFilterSchema from "permissions/_api/queries/filtering/permission-filter-schema";
import PermissionOrderParameters, {
  PermissionOrderField,
} from "permissions/_api/queries/ordering/permission-order-params";
import PermissionOrderSchema from "permissions/_api/queries/ordering/permission-order-params-schema";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import { z } from "zod";

export type PermissionQueryJSON = z.infer<typeof PermissionQuerySchema.PermissionQueryJSONZ>;

export type PermissionQueryParams = z.infer<typeof PermissionQuerySchema.PermissionQueryZ>;

export default class PermissionQuerySchema {
  public static readonly PermissionQueryJSONZ = z.object({
    filter: PermissionFilterSchema.PermissionFilterJSONZ.nullish(),
    order: PermissionOrderSchema.PermissionOrderZ.nullish(),
    pagination: PaginationSchema.PaginationZ.nullish(),
    permissionView: z.enum(PermissionView).nullish(),
    previewSize: z.number().nullish(),
  });

  public static readonly PermissionQueryZ = z.object({
    filterParameters: PermissionFilterSchema.PermissionFilterZ.optional(),
    orderParameters: PermissionOrderSchema.PermissionOrderZ.optional(),
    paginationParameters: PaginationSchema.PaginationZ.optional(),
    permissionView: z.enum(PermissionView).optional(),
    previewSize: z.number().optional(),
  });

  public static validateJSON(values: unknown): PermissionQueryJSON {
    return parseOrThrow(PermissionQuerySchema.PermissionQueryJSONZ, values, {
      logError: true,
      schemaName: "PermissionQueryJSON",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is PermissionQueryJSON {
    try {
      PermissionQuerySchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: PermissionQueryJSON): PermissionQuery {
    const parsed = PermissionQuerySchema.validateJSON(json);

    const filterParameters = new PermissionFilterParameters({
      hasParent: parsed.filter?.hasParent ?? undefined,
      resource: parsed.filter?.resource,
      parentResource: parsed.filter?.parentResource,
      parentId: Guid.fromJSON(parsed.filter?.parentId) ?? undefined,
      legalEntityId: Guid.fromJSON(parsed.filter?.legalEntityId) ?? undefined,
      granteeUserIds: parsed.filter?.granteeUserIds?.map((id) => Guid.fromJSON(id)!),
      grantorUserId: Guid.fromJSON(parsed.filter?.grantorUserId) ?? undefined,
      minCreatedDate: AHDate.fromJSON(parsed.filter?.minCreatedDate) ?? undefined,
      maxCreatedDate: AHDate.fromJSON(parsed.filter?.maxCreatedDate) ?? undefined,
      showDisabled: parsed.filter?.showDisabled ?? undefined,
      minDisabledDate: AHDate.fromJSON(parsed.filter?.minDisabledDate) ?? undefined,
      maxDisabledDate: AHDate.fromJSON(parsed.filter?.maxDisabledDate) ?? undefined,
      categories: parsed.filter?.categories ?? undefined,
      accessType: parsed.filter?.accessType ?? undefined,
      name: parsed.filter?.name ?? undefined,
    });

    const orderParameters = parsed.order
      ? new PermissionOrderParameters(parsed.order)
      : new PermissionOrderParameters({
          orderBy: PermissionOrderField.CreatedDate,
          orderDir: OrderDirection.Descending,
        });

    const paginationParameters = PaginationParameters.create({
      pageIndex: parsed.pagination?.pageIndex ?? 0,
      pageSize: parsed.pagination?.pageSize ?? 25,
    });

    return new PermissionQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
      permissionView: parsed.permissionView ?? PermissionView.Grantee,
      previewSize: parsed.previewSize ?? 10,
    });
  }

  public static serialize(query: PermissionQuery): PermissionQueryJSON {
    const { filterParameters, orderParameters, paginationParameters, permissionView, previewSize } = query;

    const json: PermissionQueryJSON = {
      filter: {
        hasParent: filterParameters?.hasParent,
        resource: filterParameters?.resource,
        parentResource: filterParameters?.parentResource,
        parentId: filterParameters?.parentId?.toJSON(),
        legalEntityId: filterParameters?.legalEntityId?.toJSON(),
        granteeUserIds: filterParameters?.granteeUserIds?.map((id: Guid) => id.toJSON()),
        grantorUserId: filterParameters?.grantorUserId?.toJSON(),
        minCreatedDate: filterParameters?.minCreatedDate?.toISOString(),
        maxCreatedDate: filterParameters?.maxCreatedDate?.toISOString(),
        showDisabled: filterParameters?.showDisabled,
        minDisabledDate: filterParameters?.minDisabledDate?.toISOString(),
        maxDisabledDate: filterParameters?.maxDisabledDate?.toISOString(),
        categories: filterParameters?.categories,
        accessType: filterParameters?.accessType,
        name: filterParameters?.name,
      },
      order: {
        orderBy: orderParameters?.orderBy,
        orderDir: orderParameters?.orderDir,
      },
      pagination: {
        pageIndex: paginationParameters?.pageIndex ?? 0,
        pageSize: paginationParameters?.pageSize ?? 25,
      },
      permissionView: permissionView,
      previewSize: previewSize,
    };

    return parseOrThrow(PermissionQuerySchema.PermissionQueryJSONZ, json, {
      logError: true,
      schemaName: "PermissionQueryJSON",
      operation: "serialize",
      payloadSample: json,
    });
  }
}
